/**
 * Olfacy collection page.
 * Filters, sort, chips and "Load more" update the results in place using the Section Rendering API
 * (only this section's HTML is fetched). Every control is a real link/form, so it also works without JS.
 */
const VIEW_KEY = 'olfacy:collection-view';

class OlfacyCollection extends HTMLElement {
  connectedCallback() {
    this.sectionId = this.dataset.sectionId;
    this.abort = new AbortController();
    const { signal } = this.abort;

    // Links that change filters (chips, clear)
    this.addEventListener('click', (event) => {
      const link = event.target.closest('a[data-ajax-link]');
      if (link) {
        event.preventDefault();
        this.render(link.href);
        return;
      }
      const more = event.target.closest('a[data-load-more]');
      if (more) {
        event.preventDefault();
        this.loadMore(more);
        return;
      }
      const opener = event.target.closest('[data-open]');
      if (opener) this.openSheet(opener.dataset.open);
      if (event.target.closest('[data-close]')) this.closeSheets();
      if (event.target.closest('[data-reset]')) this.resetMobileFilters();
      const tab = event.target.closest('[data-tab]');
      if (tab) this.selectTab(tab);
      const view = event.target.closest('[data-view-btn]');
      if (view) this.setView(view.dataset.viewBtn, true);
    }, { signal });

    // Desktop filters apply immediately; price inputs wait until the value is committed
    this.addEventListener('change', (event) => {
      const form = event.target.form;
      if (!form) return;
      if (form.matches('[data-filter-form="desktop"]')) this.render(this.urlFromForm(form));
      if (form.matches('[data-sort-form]')) {
        this.closeSheets();
        this.render(this.urlWithSort(event.target.value));
      }
    }, { signal });

    // Mobile filter screen applies on "Apply Filters"
    this.addEventListener('submit', (event) => {
      const form = event.target;
      if (form.matches('[data-filter-form="mobile"]')) {
        event.preventDefault();
        this.closeSheets();
        this.render(this.urlFromForm(form));
      }
    }, { signal });

    // Close sheets by tapping the backdrop
    this.querySelectorAll('dialog').forEach((dialog) =>
      dialog.addEventListener('click', (event) => { if (event.target === dialog) dialog.close(); }, { signal })
    );

    window.addEventListener('popstate', () => this.render(location.href, false), { signal });

    let saved = 'grid';
    try { saved = localStorage.getItem(VIEW_KEY) || 'grid'; } catch {}
    this.setView(saved, false);
  }

  disconnectedCallback() {
    this.abort?.abort();
    this.controller?.abort();
  }

  /* ---------- URLs ---------- */
  urlFromForm(form) {
    const params = new URLSearchParams();
    for (const [key, value] of new FormData(form)) {
      if (value !== '') params.append(key, value);
    }
    const sort = new URL(location.href).searchParams.get('sort_by');
    if (sort) params.set('sort_by', sort);
    const query = params.toString();
    return `${location.pathname}${query ? `?${query}` : ''}`;
  }

  urlWithSort(value) {
    const url = new URL(location.href);
    url.searchParams.set('sort_by', value);
    url.searchParams.delete('page');
    return `${url.pathname}${url.search}`;
  }

  async fetchSection(url) {
    this.controller?.abort();
    this.controller = new AbortController();
    const target = new URL(url, location.origin);
    target.searchParams.set('section_id', this.sectionId);
    const response = await fetch(target, { signal: this.controller.signal });
    const html = await response.text();
    return new DOMParser().parseFromString(html, 'text/html').querySelector('olfacy-collection');
  }

  /* ---------- Rendering ---------- */
  async render(url, push = true) {
    const main = this.querySelector('[data-results]');
    main?.setAttribute('aria-busy', 'true');
    try {
      const fresh = await this.fetchSection(url);
      if (!fresh) return (location.href = url);

      // Keep open accordions open across updates
      const open = [...this.querySelectorAll('.ocol__filters details[open]')].map((d) => d.id);

      for (const selector of ['[data-results]', '[data-results-chips]', '[data-results-filters]', '[data-results-mobile-filters]', '.ocol__pill', '[data-sort-select]']) {
        const current = this.querySelector(selector);
        const next = fresh.querySelector(selector);
        if (current && next) current.replaceWith(next);
      }
      // Sort radios on mobile
      const sortValue = new URL(url, location.origin).searchParams.get('sort_by');
      if (sortValue) this.querySelectorAll('[data-sort-form="mobile"] input').forEach((i) => (i.checked = i.value === sortValue));

      open.forEach((id) => { const d = document.getElementById(id); if (d) d.open = true; });
      this.setView(this.dataset.view || 'grid', false);
      if (push) history.pushState({}, '', url);
    } catch (error) {
      if (error.name !== 'AbortError') location.href = url;
    } finally {
      this.querySelector('[data-results]')?.removeAttribute('aria-busy');
    }
  }

  async loadMore(link) {
    link.setAttribute('aria-busy', 'true');
    try {
      const fresh = await this.fetchSection(link.href);
      const items = fresh?.querySelectorAll('[data-grid] > li');
      const grid = this.querySelector('[data-grid]');
      if (!items || !grid) return (location.href = link.href);
      grid.append(...items);
      const nextMore = fresh.querySelector('.ocol__more');
      const currentMore = this.querySelector('.ocol__more');
      if (nextMore) currentMore?.replaceWith(nextMore);
      else currentMore?.remove();
      history.replaceState({}, '', link.href);
    } catch (error) {
      if (error.name !== 'AbortError') location.href = link.href;
    }
  }

  /* ---------- Mobile sheets ---------- */
  openSheet(name) {
    const dialog = this.querySelector(`dialog[data-sheet="${name}"]`);
    if (dialog && !dialog.open) dialog.showModal();
  }

  closeSheets() {
    this.querySelectorAll('dialog[open]').forEach((d) => d.close());
  }

  resetMobileFilters() {
    const form = this.querySelector('[data-filter-form="mobile"]');
    form?.querySelectorAll('input[type="checkbox"]').forEach((i) => (i.checked = false));
    form?.querySelectorAll('input[data-price]').forEach((i) => (i.value = ''));
    // Apply straight away: same page, no filters, current sort kept
    const sort = new URL(location.href).searchParams.get('sort_by');
    this.closeSheets();
    this.render(`${location.pathname}${sort ? `?sort_by=${encodeURIComponent(sort)}` : ''}`);
  }

  selectTab(tab) {
    const tabs = [...this.querySelectorAll('[data-tab]')];
    tabs.forEach((t) => {
      const active = t === tab;
      t.setAttribute('aria-selected', String(active));
      const panel = document.getElementById(t.getAttribute('aria-controls'));
      if (panel) panel.hidden = !active;
    });
  }

  /* ---------- Grid / list view (mobile) ---------- */
  setView(view, remember) {
    this.dataset.view = view;
    this.querySelectorAll('[data-view-btn]').forEach((b) => b.setAttribute('aria-pressed', String(b.dataset.viewBtn === view)));
    if (remember) {
      try { localStorage.setItem(VIEW_KEY, view); } catch {}
    }
  }
}

if (!customElements.get('olfacy-collection')) {
  customElements.define('olfacy-collection', OlfacyCollection);
}
